/**
 * @file Abstract base class for input
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { EventEmitter, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { AbstractBase } from '../../../core/helpers/abstract-base';
/**
 * Abstract base class for input
 */
var InputBase = /** @class */ (function (_super) {
    tslib_1.__extends(InputBase, _super);
    function InputBase() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /** The associated angular form control. */
        _this.formControl = new FormControl();
        /** Gets sets the flag that indicates whether the value is required. */
        _this.required = false;
        /** Is input in read only mode */
        _this.readOnly = false;
        /**
         * Gets, sets component in disable mode.
         * @memberOf InputBase
         */
        _this.disabled = false;
        /** Collection of of validation errors.  */
        _this._validationErrors = [];
        /** Event to notify changes of value */
        _this.valueChange = new EventEmitter();
        /** Reference to the native html element that handles the display of the value. */
        _this.input = null;
        /** Whether or not the defaultControlValidator is used. */
        _this.usingDefaultValidator = false;
        return _this;
    }
    Object.defineProperty(InputBase.prototype, "control", {
        /** Gets the form control. */
        get: function () {
            return this.formControl;
        },
        /** Sets the form control. */
        set: function (value) {
            if (!value) {
                return;
            }
            this.setControl(value);
            this.initializeValidator();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sets the formControl. Override this method when needed.
     * @protected
     * @param {AbstractControl} value The control to set.
     * @memberOf InputBase
     * */
    InputBase.prototype.setControl = function (value) {
        this.ngOnDestroy();
        this.usingDefaultValidator = false;
        this.formControl = value;
    };
    Object.defineProperty(InputBase.prototype, "value", {
        /** Gets the value of the input component. */
        get: function () {
            return this.getValue();
        },
        /**
         * Sets the value of the input component.
         * The value property is a public twoway, input/output,  property.
         */
        set: function (val) {
            this.setValue(val);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputBase.prototype, "validationErrors", {
        /**
         * Gets an array of validation errors. The error messages are in the right language.
         * @readonly
         * @type {string[]}
         */
        get: function () {
            if (!this.formControl) {
                return [];
            }
            if (this.formControl.dirty || this.formControl.touched) {
                return this._validationErrors;
            }
            return [];
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Receive value from the HTML input element and trigger a valueChange event
     * @param event The HTML input event
     */
    InputBase.prototype.valueInput = function (event, value) {
        if (value == null) {
            this.input = event.target.value;
        }
        else {
            // Bij de radio input verandert angular de value naar 'on'.
            // Nu hebben we een work around.
            this.input = value;
        }
        if (this.isValidInput()) {
            // Updating the value bind of the entity.
            this.formControl.setValue(this.parseValue(this.input));
            this.valueChange.emit(this.value);
            // Ensure to get errors of the current entity state.
            this.getValidatorErrors();
        }
    };
    /** Lifecycle hook that is called when a directive, pipe or service is destroyed. */
    InputBase.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
        this.subscriptionPool.unsubscribeAll();
        if (this.usingDefaultValidator) {
            this.formControl.clearValidators();
        }
        this.formControl = null;
    };
    /**
     * Indicated whether or not control is in error state.
     * @returns {boolean} True when control is in error state.
     */
    InputBase.prototype.hasErrors = function () {
        // Following solutions give rise to angular life cycle problems:
        // return this.formControl.status === 'INVALID'
        // return (this.validationErrors && this.validationErrors.length > 0);
        // return this._hasErrors;
        return (this.validationErrors.length !== 0);
    };
    /**
     * Gets the displayed value.
     * Override this method for specific types when needed.
     * @protected
     * @returns {*} Displayed value
     * @memberOf InputBase
    * */
    InputBase.prototype.getValue = function () {
        return this.formControl ? this.formControl.value : undefined; // Fix for #3475
    };
    /**
     * Set the value of the input.
     * Override this method when extending the value behavior.
     * @param val The new value to set.
     */
    InputBase.prototype.setValue = function (val) {
        this.input = val;
        if (this.value === val) {
            return;
        }
        if (this.formControl) {
            this.formControl.setValue(val);
        }
        this.valueChange.emit(val);
        // Ensure to get errors of the current entity state.
        this.getValidatorErrors();
    };
    /**
     * Override this method to give a specific input its validator.
     */
    InputBase.prototype.defaultControlValidator = function () {
        var self = this;
        return function (c) {
            return self.isValidValue(c.value);
        };
    };
    /**
     * @returns A value indicating if the input has content and if it is valid
     */
    InputBase.prototype.hasValidContent = function () {
        if (this.required && this.value === null) {
            return false;
        }
        return this.isValidInput();
    };
    /**
     * Validates the input
     * @returns A value indicating whether the input is valid (and can be parsed)
     */
    InputBase.prototype.isValidInput = function () {
        if (this.required && this.value === null) {
            return false;
        }
        else {
            return true;
        }
    };
    /**
     * Validates a value
     * @param newValue The value to be validated
     * @returns A value indicating whether the value is valid
     */
    InputBase.prototype.isValidValue = function (newValue) {
        return null;
    };
    /**
     * Parses a new value into its appropriate type
     * @param newValue The value to be parsed
     * @returns The parsed value
     */
    InputBase.prototype.parseValue = function (newValue) {
        return newValue;
    };
    /**
     * Overrides the default validator when the control already has one.
     * Override this method if another behavior is needed.
     * @protected
     */
    InputBase.prototype.initializeValidator = function () {
        if (this.formControl.validator) {
            return;
        }
        this.usingDefaultValidator = true;
        this.formControl.setValidators(this.defaultControlValidator());
    };
    /**
     * Gets validation errors by revalidating the validator of the control.
     * In this way we get a validation of the current entity state and current control state.
     * @protected
     * @returns {ValidationErrors} Validation errors or null when there are none.
     */
    InputBase.prototype.getValidatorErrors = function () {
        if (this.formControl) {
            var validator = this.formControl.validator;
            if (validator) {
                var errors = validator(this.formControl);
                if (errors) {
                    this.publishErrors(errors);
                    return errors;
                }
            }
        }
        var inputErrors = this.isValidValue(this.input);
        if (inputErrors) {
            this.formControl.setErrors(inputErrors);
            this.publishErrors(inputErrors);
            return inputErrors;
        }
        this.clearErrors();
        return null;
    };
    /**
     * Publishes the validation messages the the validationErrors list
     */
    InputBase.prototype.publishErrors = function (errors) {
        var _this = this;
        this.clearErrors();
        Object.keys(errors).forEach(function (errorKey) {
            _this._validationErrors.push(errors[errorKey]);
        });
    };
    /**
     * Clears the validationErrors list.
     */
    InputBase.prototype.clearErrors = function () {
        this._validationErrors = [];
    };
    return InputBase;
}(AbstractBase));
export { InputBase };
